import { useCallback, useEffect, useRef, useState } from "react";
import { computeGhostGuideSteps } from "../engine/slidingGhostGuideSolver";
import {
    GHOST_GUIDE_HOLD_ALL_MS,
    runGhostGuideSequence,
    sleep,
} from "../engine/slidingGhostGuideEffects";
import { HINT_POINT_COST, HINT_POINTS_FEATURE_GHOST_GUIDE } from "./hintPointsConstants";
import { GHOST_GUIDE_TEST_MODE } from "./ghostGuideTestConfig";
import Logger from "../../../../services/Logger";

/**
 * Build ordered marks for the board: `{ [sliceIndex]: "1" | "2,4" | … }` (1-based).
 * A tile can appear in more than one step; keep every step order joined with commas.
 * @param {number[]} steps
 * @returns {Record<string, string>}
 */
function marksFromSteps(steps) {
    const marks = {};
    steps.forEach((sliceIndex, index) => {
        const key = String(sliceIndex);
        const order = String(index + 1);
        if (marks[key]) {
            marks[key] = `${marks[key]},${order}`;
        } else {
            marks[key] = order;
        }
    });
    return marks;
}

/**
 * Ghost Guide — flashes the next optimal taps (up to 10, or fewer if solved sooner)
 * with order numbers, then holds all guided cells visible. Board stays playable
 * so the player can follow the numbered moves.
 */
export function useGhostGuideAssistant({
    gridSize,
    getLayout,
    emptySliceIdx = null,
    pointBalance = 0,
    enabled = true,
    testMode = GHOST_GUIDE_TEST_MODE,
    spendPoints = null,
    onPointBalanceChange = null,
    onInsufficientPoints = null,
} = {}) {
    const [ghostGuideActive, setGhostGuideActive] = useState(false);
    /** @type {[Record<string, number|string>, Function]} sliceIndex → step order label (1-based) */
    const [ghostGuideMarks, setGhostGuideMarks] = useState({});
    const sequenceControlRef = useRef({ aborted: false });

    useEffect(
        () => () => {
            sequenceControlRef.current.aborted = true;
        },
        [],
    );

    const canAfford = testMode || pointBalance >= HINT_POINT_COST;
    const buttonDisabled = !enabled || ghostGuideActive || (!testMode && !canAfford);

    const activateGhostGuide = useCallback(async () => {
        if (!enabled || ghostGuideActive || (!testMode && !canAfford)) {
            return false;
        }

        const layout = typeof getLayout === "function" ? getLayout() : null;
        if (!Array.isArray(layout) || layout.length !== gridSize * gridSize) {
            return false;
        }

        const steps = computeGhostGuideSteps(layout, gridSize, emptySliceIdx);
        if (!steps.length) {
            return false;
        }

        setGhostGuideActive(true);
        setGhostGuideMarks({});
        sequenceControlRef.current = { aborted: false };

        if (!testMode) {
            try {
                const spend = spendPoints;
                if (typeof spend !== "function") {
                    throw new Error("No hint spend handler.");
                }
                const result = await spend({
                    featureUsed: HINT_POINTS_FEATURE_GHOST_GUIDE,
                    pointsConsumed: HINT_POINT_COST,
                });
                if (typeof onPointBalanceChange === "function") {
                    onPointBalanceChange(result.pointBalance);
                }
            } catch (e) {
                Logger.warn("useGhostGuideAssistant.consume", e);
                setGhostGuideActive(false);
                if (typeof onInsufficientPoints === "function") {
                    onInsufficientPoints(e);
                }
                return false;
            }
        }

        await runGhostGuideSequence(
            steps,
            {
                onHighlight: (sliceIndex, stepOrder) => {
                    if (sliceIndex == null || stepOrder == null) {
                        setGhostGuideMarks({});
                        return;
                    }
                    setGhostGuideMarks({ [String(sliceIndex)]: stepOrder });
                },
                onComplete: () => {},
            },
            sequenceControlRef.current,
        );

        if (!sequenceControlRef.current.aborted) {
            setGhostGuideMarks(marksFromSteps(steps));
            await sleep(GHOST_GUIDE_HOLD_ALL_MS);
        }

        setGhostGuideMarks({});
        setGhostGuideActive(false);
        return true;
    }, [
        canAfford,
        emptySliceIdx,
        enabled,
        getLayout,
        ghostGuideActive,
        gridSize,
        onInsufficientPoints,
        onPointBalanceChange,
        spendPoints,
        testMode,
    ]);

    return {
        pointBalance,
        ghostGuideActive,
        ghostGuideMarks,
        /** Hints stay visible while the player follows them — do not lock the board. */
        boardInputLocked: false,
        buttonDisabled,
        canAfford,
        activateGhostGuide,
    };
}
